import React, { useState, useEffect } from "react";
import { _getBaseUrl, _makeId, _isFirstRender } from "../utils/helper";

const CheckboxTree = ({ data, values, ...props }) => {
  const randomId = _makeId(10);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const [expandedCheckboxIds, setExpandedCheckboxIds] = useState(new Set());
  const isFirstLoad = _isFirstRender();

  useEffect(() => {
    // update state with values from parent
    setSelectedCheckboxes(values || []);

    if (Array.isArray(values) && values.length > 0 && isFirstLoad) {
      // expand tree to see preselected checkboxes
      const preselectedQids = values.map((item) => item.id);
      const ancestorTids = getAncestorTids(data, preselectedQids);

      if (ancestorTids) {
        setExpandedCheckboxIds((prevExpandedCheckboxIds) => {
          const updatedSet = new Set(prevExpandedCheckboxIds);
          ancestorTids.map((id) => {
            updatedSet.add(id);
          });
          return updatedSet;
        });
      }
    }
  }, [values]);

  useEffect(() => {
    if (props?.disabled === true) {
      resetAll();
    }
  }, [props?.disabled]);

  const handleCheckboxChange = (event, value, itemData) => {
    const isChecked = event.target.checked;
    let updatedSelectedCheckboxes = [...selectedCheckboxes];

    if (isChecked) {
      updatedSelectedCheckboxes.push(value);
    } else {
      updatedSelectedCheckboxes = updatedSelectedCheckboxes.filter(
        (element) => element.id !== value.id
      );
    }

    // Select or deselect children checkboxes
    const childrenValues = getAllNestedChildrenIds(itemData);

    childrenValues.forEach((child) => {
      if (isChecked) {
        updatedSelectedCheckboxes.push(child);
      } else {
        updatedSelectedCheckboxes = updatedSelectedCheckboxes.filter(
          (element) => element.id !== child.id
        );
      }
    });

    setSelectedCheckboxes(updatedSelectedCheckboxes);
    // dont use useEffect to update to parent - creates a loop when loading current values from parent
    props.onChange(updatedSelectedCheckboxes, {
      ...value,
      checked: isChecked,
    });
  };

  const handleToggle = (id) => {
    setExpandedCheckboxIds((prevExpandedCheckboxIds) => {
      const updatedSet = new Set(prevExpandedCheckboxIds);
      if (updatedSet.has(id)) {
        updatedSet.delete(id);
      } else {
        updatedSet.add(id);
      }
      return updatedSet;
    });
  };

  const expandAll = () => {
    const allCheckboxIDs = getAllCheckboxIDs(data);
    setExpandedCheckboxIds(new Set(allCheckboxIDs));
  };

  const collapseAll = () => {
    setExpandedCheckboxIds(new Set());
  };

  const resetAll = () => {
    setSelectedCheckboxes([]);
    props.onChange([]);
  };

  const selectAll = () => {
    const allCheckboxValues = getAllCheckboxIDs(data, true);
    setSelectedCheckboxes(allCheckboxValues);
    props.onChange(allCheckboxValues);

    if (props.onSelectAll) {
      props.onSelectAll(allCheckboxValues);
    }
  };

  const getAllCheckboxIDs = (items, itemDetails = false) => {
    let checkboxIDs = [];

    items.forEach((item) => {
      const id = item.tid || item.qid || item.cid || item.value;
      const idType = item.tid
        ? "tid"
        : item.qid
        ? "qid"
        : item.cid
        ? "cid"
        : item.value
        ? "value"
        : "id";

      if (itemDetails) {
        checkboxIDs.push({ id, idType });
      } else {
        checkboxIDs.push(id);
      }

      if (item.children) {
        const childCheckboxIDs = getAllCheckboxIDs(item.children, itemDetails);
        checkboxIDs = [...checkboxIDs, ...childCheckboxIDs];
      }
    });

    return checkboxIDs;
  };

  const getAllNestedChildrenIds = (item) => {
    let childrenIds = [];

    if (item.children) {
      item.children.forEach((child) => {
        const childId = child.tid || child.qid || child.cid || child.value;
        const idType = child.tid
          ? "tid"
          : child.qid
          ? "qid"
          : child.cid
          ? "cid"
          : child.value
          ? "value"
          : "id";

        // MUST match item structure in selectedCheckboxes
        childrenIds.push({ id: childId, idType });

        if (child.children) {
          const nestedChildrenIds = getAllNestedChildrenIds(child);
          childrenIds = childrenIds.concat(nestedChildrenIds);
        }
      });
    }

    return childrenIds;
  };

  const getAncestorTids = (tree, qids, ancestorTids = []) => {
    const findAncestorIds = (node, qid, ancestorIds = []) => {
      if (node.children) {
        for (const child of node.children) {
          if (child.qid === qid) {
            return [...ancestorIds, node.tid];
          }
          const foundAncestorIds = findAncestorIds(child, qid, [
            ...ancestorIds,
            node.tid,
          ]);
          if (foundAncestorIds) {
            return foundAncestorIds;
          }
        }
      }
      return null;
    };

    for (const item of tree) {
      for (const qid of qids) {
        const ancestorIds = findAncestorIds(item, qid);
        if (ancestorIds) {
          ancestorTids.push(...ancestorIds);
        }
      }
    }

    return ancestorTids;
  };

  const renderCheckboxes = (items, level = 0) => {
    let isDisabled = props?.disabled ?? false;

    return items.map((item) => {
      const id = item.tid || item.qid || item.cid || item.value;
      const idType = item.tid
        ? "tid"
        : item.qid
        ? "qid"
        : item.cid
        ? "cid"
        : item.value
        ? "value"
        : "id";

      const hasChildren = item.children && item.children.length > 0;
      let isChecked =
        selectedCheckboxes.length > 0 &&
        selectedCheckboxes.some((item) => item.id === id);
      const isExpanded = expandedCheckboxIds.has(id);
      const children = item.children;

      // if you want to disable specific checkbox ids
      if (
        props?.disabled &&
        Array.isArray(props?.disabled) &&
        props?.disabled.length > 0
      ) {
        const disabledOptions = props.disabled;
        if (disabledOptions.includes(id)) {
          isDisabled = true;
        } else {
          isDisabled = false;
        }
      }

      return (
        <div
          className={`item__${id} ${
            props.itemSpacing ? props.itemSpacing : "my-3"
          }`}
          key={id}
        >
          <div className="checkbox-input-wrap flex">
            {hasChildren && (
              <button
                className={`toggle-subtree mr-4 ${
                  isExpanded ? "expanded rotate-90" : "collapsed rotate-0"
                }`}
                onClick={() => handleToggle(id)}
              >
                <img
                  src={_getBaseUrl(
                    "src/assets/icons/icon-chevron-right-dark.svg",
                    true
                  )}
                  alt="toggle subtree"
                />
              </button>
            )}
            <input
              type="checkbox"
              id={randomId + "_" + id}
              checked={isChecked}
              onChange={(event) =>
                handleCheckboxChange(event, { id, idType }, item)
              }
              disabled={isDisabled}
            />
            <label htmlFor={randomId + "_" + id}></label>
            <label htmlFor={randomId + "_" + id}>{item.name}</label>
          </div>
          {hasChildren && isExpanded && (
            <div className="subtree ml-10">
              {renderCheckboxes(children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div>
      {!props.disableActionButtons && (
        <div className="flex gap-y-4 gap-x-8 mt-4 mb-6 text-desktopS">
          <button
            className="tema-text--link"
            onClick={
              props.actionButtonsType === "selectAll" ? selectAll : expandAll
            }
          >
            {props.actionButtonsType === "selectAll"
              ? "Select all"
              : "Expand all"}
          </button>
          {props.actionButtonsType !== "selectAll" && (
            <button className="tema-text--link" onClick={collapseAll}>
              Collapse all
            </button>
          )}
          <button className="tema-text--link" onClick={resetAll}>
            Reset
          </button>
        </div>
      )}

      <div
        className={`checkbox-tree tema-form text-desktopS ${
          props.treeClass ?? ""
        }`}
      >
        {renderCheckboxes(data)}
      </div>

      {typeof props.error === "string" && (
        <div className="form-error text-red-500 text-desktopXS mt-2">
          {props.error}
        </div>
      )}
    </div>
  );
};

export default CheckboxTree;
